import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useCompartilhamentos, CompartilhadaBadge } from './CompartilhadaBadge';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { 
  Plus, 
  Wrench, 
  Search,
  ChevronDown,
  ChevronUp,
  Calendar,
  DollarSign,
  Clock,
  Edit,
  Trash2,
  RefreshCw,
  Gauge,
  History,
  Filter,
  Bike,
  FileText,
  TrendingUp,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  CalendarDays,
  LayoutList,
  User
} from 'lucide-react';
import { apiService } from '../../services/api';

// Tipos de manutenção com cores
const TIPOS_MANUTENCAO = {
  'preventiva': { label: 'Preventiva', color: 'bg-blue-100 text-blue-800', icon: '🔧' },
  'corretiva': { label: 'Corretiva', color: 'bg-red-100 text-red-800', icon: '🔴' },
  'troca_oleo': { label: 'Troca de Óleo', color: 'bg-green-100 text-green-800', icon: '🛢️' },
  'pneu': { label: 'Pneu', color: 'bg-yellow-100 text-yellow-800', icon: '⚫' },
  'freio': { label: 'Freio', color: 'bg-orange-100 text-orange-800', icon: '🛑' },
  'eletrica': { label: 'Elétrica', color: 'bg-purple-100 text-purple-800', icon: '⚡' },
  'suspensao': { label: 'Suspensão', color: 'bg-indigo-100 text-indigo-800', icon: '🔩' },
  'outros': { label: 'Outros', color: 'bg-slate-700/50 text-white', icon: '📋' }
};

export const ManutencaoPageNova = () => {
  const compartilhamentos = useCompartilhamentos();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSubAdmin = user?.tipo === 'sub_admin';

  const [manutencoes, setManutencoes] = useState([]);
  const [motos, setMotos] = useState([]);
  const [locacoes, setLocacoes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipo, setFilterTipo] = useState('todos');
  const [expandedMotos, setExpandedMotos] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [editingManutencao, setEditingManutencao] = useState(null);
  const [selectedMotoId, setSelectedMotoId] = useState(null);
  
  // Modo de visualização: 'moto' ou 'diario'
  const [viewMode, setViewMode] = useState('moto');
  
  // Filtro de data para visão diária
  const [filterMes, setFilterMes] = useState(() => {
    const hoje = new Date();
    return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
  });
  
  // Estado para confirmação extra
  const [showConfirmacaoModal, setShowConfirmacaoModal] = useState(false);
  const [dadosParaSalvar, setDadosParaSalvar] = useState(null);
  
  const [formData, setFormData] = useState({
    moto_id: '',
    tipo: 'preventiva',
    descricao: '',
    data_realizada: new Date().toISOString().split('T')[0],
    km_realizada: '',
    custo: '',
    responsavel: '',
    observacoes: ''
  });

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [motosData, manutencoesData, locacoesData, clientesData] = await Promise.all([
        apiService.getMotos(),
        apiService.getManutencoes(),
        apiService.getLocacoes(),
        apiService.getClientes()
      ]);
      setMotos(motosData || []);
      setManutencoes(manutencoesData || []);
      setLocacoes(locacoesData || []);
      setClientes(clientesData || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  // Função para obter o nome do cliente da moto (via locação ativa)
  const getClienteNome = (motoId) => {
    const locacao = locacoes.find(l => l.moto_id === motoId && l.status === 'ativa');
    if (locacao) {
      const cliente = clientes.find(c => c.id === locacao.cliente_id);
      return cliente?.nome || 'Sem locatário';
    }
    return 'Disponível';
  };

  // Agrupa manutenções por moto
  const manutencoesPorMoto = useMemo(() => {
    const agrupado = {};
    
    motos.forEach(moto => {
      const motoManutencoes = manutencoes.filter(m => 
        m.moto_id === moto.id || m.moto_placa === moto.placa
      );
      
      // Conta por tipo
      const contagem = {};
      Object.keys(TIPOS_MANUTENCAO).forEach(tipo => {
        contagem[tipo] = motoManutencoes.filter(m => m.tipo === tipo).length;
      });
      
      // Calcula custo total
      const custoTotal = motoManutencoes.reduce((acc, m) => acc + (parseFloat(m.custo) || 0), 0);
      
      // Ordena por data (mais recente primeiro)
      const ordenadas = [...motoManutencoes].sort((a, b) => 
        new Date(b.data_realizada || b.data_manutencao) - new Date(a.data_realizada || a.data_manutencao)
      );
      
      agrupado[moto.id] = {
        moto,
        manutencoes: ordenadas,
        contagem,
        custoTotal,
        totalManutencoes: motoManutencoes.length,
        ultimaManutencao: ordenadas[0]
      };
    });
    
    return agrupado;
  }, [motos, manutencoes]);

  // Filtra motos baseado na busca
  const motosFiltradas = useMemo(() => {
    return Object.values(manutencoesPorMoto).filter(({ moto, manutencoes: manutencoesLista }) => {
      const matchSearch = !searchTerm || 
        moto.placa?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        moto.modelo?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchTipo = filterTipo === 'todos' || 
        manutencoesLista.some(m => m.tipo === filterTipo);
      
      return matchSearch && matchTipo;
    }).sort((a, b) => b.totalManutencoes - a.totalManutencoes);
  }, [manutencoesPorMoto, searchTerm, filterTipo]);

  // Estatísticas gerais
  const estatisticas = useMemo(() => {
    const total = manutencoes.length;
    const custoTotal = manutencoes.reduce((acc, m) => acc + (parseFloat(m.custo) || 0), 0);
    const esteMes = manutencoes.filter(m => {
      const data = new Date(m.data_realizada || m.data_manutencao);
      const agora = new Date();
      return data.getMonth() === agora.getMonth() && data.getFullYear() === agora.getFullYear();
    }).length;
    
    return { total, custoTotal, esteMes };
  }, [manutencoes]);

  // Agrupa manutenções por data (para visão diária)
  const manutencoesPorDia = useMemo(() => {
    // Filtra pelo mês selecionado
    const [ano, mes] = filterMes.split('-').map(Number);
    
    const filtradas = manutencoes.filter(m => {
      const data = new Date(m.data_realizada || m.data_manutencao);
      return data.getFullYear() === ano && data.getMonth() === mes - 1;
    });
    
    // Agrupa por data
    const agrupado = {};
    filtradas.forEach(m => {
      const dataStr = m.data_realizada || m.data_manutencao;
      const dataKey = dataStr?.split('T')[0] || 'sem-data';
      
      if (!agrupado[dataKey]) {
        agrupado[dataKey] = {
          data: dataKey,
          manutencoes: [],
          custoTotal: 0
        };
      }
      
      // Encontrar moto e cliente
      const moto = motos.find(mt => mt.id === m.moto_id || mt.placa === m.moto_placa);
      const clienteNome = moto ? getClienteNome(moto.id) : 'N/A';
      
      agrupado[dataKey].manutencoes.push({
        ...m,
        moto,
        clienteNome
      });
      agrupado[dataKey].custoTotal += parseFloat(m.custo) || 0;
    });
    
    // Converte para array e ordena por data (mais recente primeiro)
    return Object.values(agrupado).sort((a, b) => 
      new Date(b.data) - new Date(a.data)
    );
  }, [manutencoes, motos, locacoes, clientes, filterMes]);

  const toggleExpand = (motoId) => {
    setExpandedMotos(prev => ({
      ...prev,
      [motoId]: !prev[motoId]
    }));
  };

  const expandirTodas = () => {
    const todas = {};
    motos.forEach(m => todas[m.id] = true);
    setExpandedMotos(todas);
  };

  const recolherTodas = () => {
    setExpandedMotos({});
  };

  const abrirModalNova = (motoId = null) => {
    setEditingManutencao(null);
    setFormData({
      moto_id: motoId || '',
      tipo: 'preventiva',
      descricao: '',
      data_realizada: new Date().toISOString().split('T')[0],
      km_realizada: '',
      custo: '',
      responsavel: '',
      observacoes: ''
    });
    setSelectedMotoId(motoId);
    setShowModal(true);
  };

  const abrirModalEditar = (manutencao) => {
    setEditingManutencao(manutencao);
    setFormData({
      moto_id: manutencao.moto_id || '',
      tipo: manutencao.tipo || 'preventiva',
      descricao: manutencao.descricao || '',
      data_realizada: manutencao.data_realizada || manutencao.data_manutencao || '',
      km_realizada: manutencao.km_realizada || '',
      custo: manutencao.custo || '',
      responsavel: manutencao.responsavel || '',
      observacoes: manutencao.observacoes || ''
    });
    setShowModal(true);
  };

  // Solicitar confirmação antes de salvar
  const salvarManutencao = async () => {
    const dados = {
      ...formData,
      km_realizada: parseInt(formData.km_realizada) || 0,
      custo: parseFloat(formData.custo) || 0
    };
    
    // Mostrar modal de confirmação
    setDadosParaSalvar(dados);
    setShowConfirmacaoModal(true);
  };

  // Executar salvamento após confirmação
  const executarSalvarManutencao = async () => {
    if (!dadosParaSalvar) return;
    
    try {
      if (editingManutencao) {
        await apiService.atualizarManutencao(editingManutencao.id, dadosParaSalvar);
      } else {
        await apiService.registrarManutencao(dadosParaSalvar);
      }
      
      setShowModal(false);
      setShowConfirmacaoModal(false);
      setDadosParaSalvar(null);
      carregarDados();
    } catch (error) {
      console.error('Erro ao salvar manutenção:', error);
      alert('Erro ao salvar manutenção');
    }
  };

  // Cancelar confirmação
  const cancelarConfirmacao = () => {
    setShowConfirmacaoModal(false);
    setDadosParaSalvar(null);
  };

  const excluirManutencao = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta manutenção?')) return;
    
    try {
      await apiService.deletarManutencao(id);
      carregarDados();
    } catch (error) {
      console.error('Erro ao excluir manutenção:', error);
      alert('Erro ao excluir manutenção');
    }
  };

  const limparManutencoesAutomaticas = async () => {
    try {
      const lista = await apiService.api.get('/manutencoes/automaticas/listar');
      const total = lista.data?.total || 0;
      if (total === 0) {
        alert('✅ Nenhum registro automático encontrado. Sua base já está limpa.');
        return;
      }
      const ok = window.confirm(
        `Foram encontrados ${total} registro(s) de manutenção criados automaticamente pelo sistema (com R$ 150,00 fixo ou marcação "Sistema/Regularização"). Deseja remover todos? Esta ação é irreversível.`
      );
      if (!ok) return;
      const r = await apiService.api.delete('/manutencoes/automaticas/limpar');
      alert(`🧹 ${r.data?.removidas || 0} registro(s) automático(s) removido(s).`);
      carregarDados();
    } catch (e) {
      alert('Erro ao limpar registros automáticos: ' + (e.response?.data?.detail || e.message));
    }
  };

  const formatarData = (data) => {
    if (!data) return '-';
    return new Date(data).toLocaleDateString('pt-BR');
  };

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6 bg-slate-800/50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-white flex items-center gap-2">
            <Wrench className="w-6 h-6 md:w-7 md:h-7 text-emerald-400" />
            Controle de Manutenção
          </h1>
          <p className="text-sm text-gray-300 mt-1">Histórico completo por moto ou por dia</p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Button onClick={carregarDados} variant="outline" size="sm" className="flex-1 sm:flex-none">
            <RefreshCw className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Atualizar</span>
          </Button>
          {!isSubAdmin && (
            <Button
              onClick={limparManutencoesAutomaticas}
              variant="outline"
              size="sm"
              className="flex-1 sm:flex-none border-red-500/40 text-red-300 hover:bg-red-500/10"
              data-testid="btn-limpar-manutencoes-automaticas"
              title="Remove registros que o sistema criava automaticamente (R$ 150,00 fixo, responsável 'Sistema')"
            >
              <AlertCircle className="w-4 h-4 mr-1 md:mr-2" />
              <span className="hidden sm:inline">Limpar Automáticas</span>
              <span className="sm:hidden">Limpar</span>
            </Button>
          )}
          <Button onClick={() => !isSubAdmin && abrirModalNova()} disabled={isSubAdmin} className={`bg-green-600 hover:bg-green-700 flex-1 sm:flex-none ${isSubAdmin ? "opacity-50 cursor-not-allowed" : ""}`}>
            <Plus className="w-4 h-4 mr-1 md:mr-2" />
            Nova Manutenção
          </Button>
        </div>
      </div>

      {/* Abas de Visualização */}
      <div className="flex gap-2 border-b border-slate-600 pb-2">
        <Button
          variant={viewMode === 'moto' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setViewMode('moto')}
          className={viewMode === 'moto' ? 'bg-emerald-600 hover:bg-emerald-700' : 'text-gray-400 hover:text-white'}
        >
          <Bike className="w-4 h-4 mr-2" />
          Por Moto
        </Button>
        <Button
          variant={viewMode === 'diario' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setViewMode('diario')}
          className={viewMode === 'diario' ? 'bg-emerald-600 hover:bg-emerald-700' : 'text-gray-400 hover:text-white'}
        >
          <CalendarDays className="w-4 h-4 mr-2" />
          Histórico Diário
        </Button>
      </div>

      {/* Estatísticas Gerais */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
        <Card className="bg-slate-700/50 border-white/10 text-white-l-4 border-l-blue-500">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-gray-400">Motos</p>
                <p className="text-lg md:text-2xl font-bold text-white">{motos.length}</p>
              </div>
              <Bike className="w-8 h-8 md:w-10 md:h-10 text-blue-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-700/50 border-white/10 text-white-l-4 border-l-green-500">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-gray-400">Manutenções</p>
                <p className="text-lg md:text-2xl font-bold text-white">{estatisticas.total}</p>
              </div>
              <Wrench className="w-8 h-8 md:w-10 md:h-10 text-green-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-700/50 border-white/10 text-white-l-4 border-l-yellow-500">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-gray-400">Este Mês</p>
                <p className="text-lg md:text-2xl font-bold text-white">{estatisticas.esteMes}</p>
              </div>
              <Calendar className="w-8 h-8 md:w-10 md:h-10 text-yellow-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-700/50 border-white/10 text-white-l-4 border-l-purple-500">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-gray-400">Custo Total</p>
                <p className="text-base md:text-2xl font-bold text-white">{formatarMoeda(estatisticas.custoTotal)}</p>
              </div>
              <DollarSign className="w-8 h-8 md:w-10 md:h-10 text-purple-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros - Visão por Moto */}
      {viewMode === 'moto' && (
        <Card className="bg-slate-800">
          <CardContent className="p-3 md:p-4">
            <div className="flex flex-col gap-3">
              {/* Busca */}
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Buscar placa ou modelo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 text-sm"
                />
              </div>
              
              {/* Filtro e botões */}
              <div className="flex flex-wrap gap-2 items-center">
                <div className="flex items-center gap-1 flex-1 min-w-[150px]">
                  <Filter className="w-4 h-4 text-gray-400 hidden sm:block" />
                  <select
                    value={filterTipo}
                    onChange={(e) => setFilterTipo(e.target.value)}
                    className="border rounded-md px-2 py-1.5 text-sm flex-1"
                  >
                    <option value="todos">Todos os tipos</option>
                    {Object.entries(TIPOS_MANUTENCAO).map(([key, { label, icon }]) => (
                      <option key={key} value={key}>{icon} {label}</option>
                    ))}
                  </select>
                </div>
                
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" onClick={expandirTodas} className="text-xs px-2">
                    <ChevronDown className="w-3 h-3 mr-1" />
                    <span className="hidden sm:inline">Expandir</span>
                  </Button>
                  <Button variant="outline" size="sm" onClick={recolherTodas} className="text-xs px-2">
                    <ChevronUp className="w-3 h-3 mr-1" />
                    <span className="hidden sm:inline">Recolher</span>
                  </Button>
                </div>
              </div>
            </div>
        </CardContent>
      </Card>
      )}

      {/* Filtros - Visão Diária */}
      {viewMode === 'diario' && (
        <Card className="bg-slate-800">
          <CardContent className="p-3 md:p-4">
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-emerald-400" />
                <span className="text-sm text-gray-300">Selecione o mês:</span>
              </div>
              <Input
                type="month"
                value={filterMes}
                onChange={(e) => setFilterMes(e.target.value)}
                className="w-full sm:w-auto"
              />
              <div className="flex-1 text-right">
                <span className="text-sm text-gray-400">
                  {manutencoesPorDia.reduce((acc, d) => acc + d.manutencoes.length, 0)} manutenções em {manutencoesPorDia.length} dia(s)
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lista de Motos com Manutenções - Visão por Moto */}
      {viewMode === 'moto' && (
      <div className="space-y-4">
        {motosFiltradas.length === 0 ? (
          <Card className="bg-slate-800">
            <CardContent className="p-8 text-center">
              <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-400">Nenhuma moto encontrada com os filtros selecionados</p>
            </CardContent>
          </Card>
        ) : (
          motosFiltradas.map(({ moto, manutencoes: motoManutencoes, contagem, custoTotal, totalManutencoes, ultimaManutencao }) => (
            <Card key={moto.id} className="bg-slate-800/90 overflow-hidden">
              {/* Header da Moto */}
              <div 
                className="p-4 cursor-pointer hover:bg-slate-800/50 transition-colors border-b"
                onClick={() => toggleExpand(moto.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Bike className="w-5 h-5 md:w-6 md:h-6 text-emerald-400" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-bold text-base md:text-lg text-white flex items-center gap-1.5 flex-wrap">
                        {moto.placa}
                        <CompartilhadaBadge nomes={compartilhamentos.por_moto[moto.id] || compartilhamentos.por_placa[moto.placa]} />
                      </h3>
                      <p className="text-xs md:text-sm text-gray-400 truncate">{moto.modelo}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 md:gap-4">
                    {/* Info resumida - mobile */}
                    <div className="flex md:hidden flex-col items-end text-xs">
                      <span className="font-bold text-emerald-400">{totalManutencoes}</span>
                      <span className="text-gray-400">manut.</span>
                    </div>
                    
                    {/* Resumo de Manutenções - desktop */}
                    <div className="hidden md:flex items-center gap-4 text-sm">
                      <div className="text-center">
                        <p className="font-bold text-lg text-emerald-400">{totalManutencoes}</p>
                        <p className="text-gray-400 text-xs">Manutenções</p>
                      </div>
                      <div className="text-center">
                        <p className="font-bold text-lg text-purple-400">{formatarMoeda(custoTotal)}</p>
                        <p className="text-gray-400 text-xs">Custo Total</p>
                      </div>
                      {ultimaManutencao && (
                        <div className="text-center">
                          <p className="font-bold text-sm text-gray-200">
                            {formatarData(ultimaManutencao.data_realizada || ultimaManutencao.data_manutencao)}
                          </p>
                          <p className="text-gray-400 text-xs">Última</p>
                        </div>
                      )}
                    </div>
                    
                    {!isSubAdmin && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          abrirModalNova(moto.id);
                        }}
                        className="hidden sm:flex text-emerald-400 hover:text-emerald-300 hover:bg-green-50"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Adicionar
                      </Button>
                    )}
                    
                    {expandedMotos[moto.id] ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
                
                {/* Info mobile expandida */}
                <div className="flex md:hidden items-center gap-3 mt-2 text-xs">
                  <Badge className="bg-slate-700/50 text-gray-200">
                    <Gauge className="w-3 h-3 mr-1" />
                    {moto.km_atual?.toLocaleString() || 0} km
                  </Badge>
                  <span className="text-purple-400 font-semibold">{formatarMoeda(custoTotal)}</span>
                  {!isSubAdmin && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        abrirModalNova(moto.id);
                      }}
                      className="sm:hidden text-emerald-400 border-green-200 text-xs h-7 px-2"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Nova
                    </Button>
                  )}
                </div>
                
                {/* Tags de tipos de manutenção */}
                <div className="flex flex-wrap gap-1 md:gap-2 mt-2 md:mt-3">
                  {Object.entries(contagem).map(([tipo, count]) => {
                    if (count === 0) return null;
                    const tipoInfo = TIPOS_MANUTENCAO[tipo] || TIPOS_MANUTENCAO['outros'];
                    return (
                      <Badge key={tipo} className={`${tipoInfo.color} text-[10px] md:text-xs`}>
                        {tipoInfo.icon} {tipoInfo.label}: {count}
                      </Badge>
                    );
                  })}
                </div>
                
                {/* Barra de Gastos com Manutenção */}
                {custoTotal > 0 && (
                  <div className="mt-3">
                    <div className="flex justify-between items-center text-xs mb-1">
                      <span className="text-gray-400 flex items-center gap-1">
                        <DollarSign className="w-3 h-3" />
                        Total Gasto em Manutenção
                      </span>
                      <span className="font-bold text-purple-400">{formatarMoeda(custoTotal)}</span>
                    </div>
                    <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-purple-500 to-purple-600 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${Math.min((custoTotal / 5000) * 100, 100)}%`
                        }}
                      />
                    </div>
                    <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                      <span>R$ 0</span>
                      <span>R$ 5.000</span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Histórico Expandido */}
              {expandedMotos[moto.id] && (
                <div className="bg-slate-800/50">
                  {motoManutencoes.length === 0 ? (
                    <div className="p-6 text-center text-gray-400">
                      <History className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p>Nenhuma manutenção registrada</p>
                      {!isSubAdmin && (
                        <Button 
                          variant="link" 
                          onClick={() => abrirModalNova(moto.id)}
                          className="text-emerald-400"
                        >
                          Registrar primeira manutenção
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="divide-y divide-white/10">
                      {/* Header da tabela - apenas desktop */}
                      <div className="hidden lg:grid grid-cols-12 gap-2 px-4 py-2 bg-slate-700/50 text-xs font-semibold text-gray-300">
                        <div className="col-span-2">DATA</div>
                        <div className="col-span-2">TIPO</div>
                        <div className="col-span-3">DESCRIÇÃO</div>
                        <div className="col-span-1 text-center">KM</div>
                        <div className="col-span-1 text-center">CUSTO</div>
                        <div className="col-span-2">RESPONSÁVEL</div>
                        <div className="col-span-1 text-center">AÇÕES</div>
                      </div>
                      
                      {motoManutencoes.map((manutencao, index) => {
                        const tipoInfo = TIPOS_MANUTENCAO[manutencao.tipo] || TIPOS_MANUTENCAO['outros'];
                        // Extrair serviços das observações
                        const temServicos = manutencao.observacoes?.includes('Serviços:');
                        const servicosTexto = temServicos 
                          ? manutencao.observacoes.split('Serviços:')[1]?.split('.')[0]?.trim()
                          : null;
                        
                        return (
                          <div key={manutencao.id || index}>
                            {/* Layout Mobile - Cards */}
                            <div className="lg:hidden p-4 hover:bg-white transition-colors">
                              <div className="flex justify-between items-start mb-2">
                                <Badge className={`${tipoInfo.color} text-xs`}>
                                  {tipoInfo.icon} {tipoInfo.label}
                                </Badge>
                                <div className="flex gap-1">
                                  {!isSubAdmin && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => abrirModalEditar(manutencao)}
                                      className="p-1 h-8 w-8"
                                    >
                                      <Edit className="w-4 h-4 text-blue-500" />
                                    </Button>
                                  )}
                                  {!isSubAdmin && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => excluirManutencao(manutencao.id)}
                                      className="p-1 h-8 w-8"
                                    >
                                      <Trash2 className="w-4 h-4 text-red-500" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                              
                              <p className="text-white font-medium mb-2">
                                {manutencao.descricao || 'Sem descrição'}
                              </p>
                              
                              {/* Serviços Realizados - Destaque */}
                              {servicosTexto && (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-2 mb-2">
                                  <p className="text-xs font-semibold text-emerald-300 mb-1">🔧 Serviços Realizados:</p>
                                  <p className="text-sm text-green-800">{servicosTexto}</p>
                                </div>
                              )}
                              
                              <div className="grid grid-cols-2 gap-2 text-sm">
                                <div className="flex items-center gap-1 text-gray-300">
                                  <Calendar className="w-3 h-3" />
                                  {formatarData(manutencao.data_realizada || manutencao.data_manutencao)}
                                </div>
                                <div className="flex items-center gap-1 text-emerald-400 font-semibold">
                                  <DollarSign className="w-3 h-3" />
                                  {formatarMoeda(manutencao.custo)}
                                </div>
                                <div className="flex items-center gap-1 text-gray-300">
                                  <Gauge className="w-3 h-3" />
                                  {manutencao.km_realizada?.toLocaleString() || '-'} km
                                </div>
                                {manutencao.responsavel && (
                                  <div className="flex items-center gap-1 text-gray-300 truncate">
                                    <Wrench className="w-3 h-3" />
                                    {manutencao.responsavel}
                                  </div>
                                )}
                              </div>
                              
                              {manutencao.observacoes && !temServicos && (
                                <p className="text-xs text-gray-400 mt-2 italic">
                                  {manutencao.observacoes}
                                </p>
                              )}
                            </div>
                            
                            {/* Layout Desktop - Tabela */}
                            <div className="hidden lg:grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-white transition-colors text-sm">
                              <div className="col-span-2 flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-gray-400" />
                                <span className="font-medium">
                                  {formatarData(manutencao.data_realizada || manutencao.data_manutencao)}
                                </span>
                              </div>
                              
                              <div className="col-span-2">
                                <Badge className={`${tipoInfo.color} text-xs`}>
                                  {tipoInfo.icon} {tipoInfo.label}
                                </Badge>
                              </div>
                              
                              <div className="col-span-3">
                                <p className="text-white truncate" title={manutencao.descricao}>
                                  {manutencao.descricao || '-'}
                                </p>
                                {servicosTexto && (
                                  <p className="text-xs text-emerald-400 font-medium truncate" title={servicosTexto}>
                                    🔧 {servicosTexto}
                                  </p>
                                )}
                              </div>
                              
                              <div className="col-span-1 text-center">
                                <span className="text-gray-200 font-medium">
                                  {manutencao.km_realizada?.toLocaleString() || '-'}
                                </span>
                              </div>
                              
                              <div className="col-span-1 text-center">
                                <span className="text-emerald-400 font-semibold">
                                  {formatarMoeda(manutencao.custo)}
                                </span>
                              </div>
                              
                              <div className="col-span-2 text-gray-300 truncate">
                                {manutencao.responsavel || '-'}
                              </div>
                              
                              <div className="col-span-1 flex justify-center gap-1">
                                {!isSubAdmin && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => abrirModalEditar(manutencao)}
                                    className="p-1 h-8 w-8"
                                  >
                                    <Edit className="w-4 h-4 text-blue-500" />
                                  </Button>
                                )}
                                {!isSubAdmin && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => excluirManutencao(manutencao.id)}
                                    className="p-1 h-8 w-8"
                                  >
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))
        )}
      </div>
      )}

      {/* Histórico Diário de Manutenções */}
      {viewMode === 'diario' && (
        <div className="space-y-4">
          {manutencoesPorDia.length === 0 ? (
            <Card className="bg-slate-800">
              <CardContent className="p-8 text-center">
                <CalendarDays className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-400">Nenhuma manutenção registrada neste mês</p>
                {!isSubAdmin && (
                  <Button 
                    variant="link" 
                    onClick={() => abrirModalNova()}
                    className="text-emerald-400 mt-2"
                  >
                    Registrar nova manutenção
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            manutencoesPorDia.map(({ data, manutencoes: manutencoesNoDia, custoTotal }) => {
              const dataFormatada = new Date(data + 'T12:00:00').toLocaleDateString('pt-BR', {
                weekday: 'long',
                day: '2-digit',
                month: 'long',
                year: 'numeric'
              });
              
              return (
                <Card key={data} className="bg-slate-800/90 overflow-hidden">
                  {/* Header do Dia */}
                  <div className="bg-gradient-to-r from-emerald-600/20 to-slate-800 p-4 border-b border-slate-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center">
                          <CalendarDays className="w-6 h-6 text-emerald-400" />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg text-white capitalize">{dataFormatada}</h3>
                          <p className="text-sm text-gray-400">
                            {manutencoesNoDia.length} manutenção(ões) realizada(s)
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-purple-400">{formatarMoeda(custoTotal)}</p>
                        <p className="text-xs text-gray-400">custo total do dia</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Lista de Manutenções do Dia */}
                  <div className="divide-y divide-slate-700/50">
                    {manutencoesNoDia.map((manutencao, index) => {
                      const tipoInfo = TIPOS_MANUTENCAO[manutencao.tipo] || TIPOS_MANUTENCAO['outros'];
                      const moto = manutencao.moto;
                      const clienteNome = manutencao.clienteNome;
                      
                      return (
                        <div 
                          key={manutencao.id || index}
                          className="p-4 hover:bg-slate-700/30 transition-colors"
                        >
                          <div className="flex flex-col md:flex-row md:items-center gap-3">
                            {/* Info da Moto e Cliente */}
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Bike className="w-5 h-5 text-blue-400" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-bold text-white">{moto?.placa || 'N/A'}</span>
                                  <Badge className={`${tipoInfo.color} text-xs`}>
                                    {tipoInfo.icon} {tipoInfo.label}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-gray-400 mt-0.5">
                                  <User className="w-3 h-3" />
                                  <span className="truncate">{clienteNome}</span>
                                  {moto?.modelo && (
                                    <>
                                      <span className="text-gray-600">•</span>
                                      <span className="truncate">{moto.modelo}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            {/* Descrição */}
                            <div className="flex-1 min-w-0">
                              <p className="text-white font-medium truncate">
                                {manutencao.descricao || 'Sem descrição'}
                              </p>
                              {manutencao.responsavel && (
                                <p className="text-xs text-gray-400 mt-0.5">
                                  <Wrench className="w-3 h-3 inline mr-1" />
                                  {manutencao.responsavel}
                                </p>
                              )}
                            </div>
                            
                            {/* Valor e KM */}
                            <div className="flex items-center gap-4 md:gap-6">
                              {manutencao.km_realizada > 0 && (
                                <div className="text-center">
                                  <p className="text-sm font-medium text-gray-200">
                                    {manutencao.km_realizada?.toLocaleString()} km
                                  </p>
                                  <p className="text-xs text-gray-500">quilometragem</p>
                                </div>
                              )}
                              <div className="text-right">
                                <p className="text-lg font-bold text-emerald-400">
                                  {formatarMoeda(manutencao.custo)}
                                </p>
                              </div>
                              <div className="flex gap-1">
                                {!isSubAdmin && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => abrirModalEditar(manutencao)}
                                    className="p-1 h-8 w-8"
                                  >
                                    <Edit className="w-4 h-4 text-blue-500" />
                                  </Button>
                                )}
                                {!isSubAdmin && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => excluirManutencao(manutencao.id)}
                                    className="p-1 h-8 w-8"
                                  >
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              );
            })
          )}
          
          {/* Resumo do Mês */}
          {manutencoesPorDia.length > 0 && (
            <Card className="bg-gradient-to-r from-purple-900/50 to-slate-800 border-purple-500/30">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="w-6 h-6 text-purple-400" />
                    <div>
                      <h4 className="font-bold text-white">Resumo do Mês</h4>
                      <p className="text-sm text-gray-400">
                        {new Date(filterMes + '-01').toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6 text-center">
                    <div>
                      <p className="text-2xl font-bold text-emerald-400">
                        {manutencoesPorDia.reduce((acc, d) => acc + d.manutencoes.length, 0)}
                      </p>
                      <p className="text-xs text-gray-400">manutenções</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-blue-400">
                        {manutencoesPorDia.length}
                      </p>
                      <p className="text-xs text-gray-400">dias com serviço</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-purple-400">
                        {formatarMoeda(manutencoesPorDia.reduce((acc, d) => acc + d.custoTotal, 0))}
                      </p>
                      <p className="text-xs text-gray-400">custo total</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Modal de Manutenção */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-emerald-400" />
              {editingManutencao ? 'Editar Manutenção' : 'Nova Manutenção'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>Moto *</Label>
              <select
                value={formData.moto_id}
                onChange={(e) => setFormData({ ...formData, moto_id: e.target.value })}
                className="w-full border rounded-md px-3 py-2 mt-1"
                required
              >
                <option value="">Selecione a moto</option>
                {motos.map(moto => (
                  <option key={moto.id} value={moto.id}>
                    {moto.placa} - {moto.modelo}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo *</Label>
                <select
                  value={formData.tipo}
                  onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                  className="w-full border rounded-md px-3 py-2 mt-1"
                >
                  {Object.entries(TIPOS_MANUTENCAO).map(([key, { label, icon }]) => (
                    <option key={key} value={key}>{icon} {label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label>Data *</Label>
                <Input
                  type="date"
                  value={formData.data_realizada}
                  onChange={(e) => setFormData({ ...formData, data_realizada: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Descrição *</Label>
              <Input
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                placeholder="Ex: Troca de óleo e filtro"
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quilometragem</Label>
                <Input
                  type="number"
                  value={formData.km_realizada}
                  onChange={(e) => setFormData({ ...formData, km_realizada: e.target.value })}
                  placeholder="Ex: 5000"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Custo (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.custo}
                  onChange={(e) => setFormData({ ...formData, custo: e.target.value })}
                  placeholder="Ex: 150.00"
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Responsável</Label>
              <Input
                value={formData.responsavel}
                onChange={(e) => setFormData({ ...formData, responsavel: e.target.value })}
                placeholder="Nome do mecânico ou oficina"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Observações</Label>
              <Textarea
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                placeholder="Observações adicionais..."
                className="mt-1"
                rows={3}
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={salvarManutencao}
                className="bg-green-600 hover:bg-green-700"
                disabled={!formData.moto_id || !formData.descricao}
              >
                {editingManutencao ? 'Salvar Alterações' : 'Registrar Manutenção'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação Extra */}
      <Dialog open={showConfirmacaoModal} onOpenChange={setShowConfirmacaoModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-500">
              <AlertTriangle className="h-5 w-5" />
              Confirmação Necessária
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <p className="text-gray-300 mb-4">
              Esta ação não pode ser desfeita. Por favor, confirme que deseja continuar.
            </p>

            {dadosParaSalvar && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 space-y-2">
                <p className="text-blue-300 font-semibold">
                  {editingManutencao ? 'Salvar alterações na manutenção?' : 'Registrar nova manutenção?'}
                </p>
                <p className="text-gray-300 text-sm">
                  Moto: <strong>{motos.find(m => m.id === dadosParaSalvar.moto_id)?.placa || 'N/A'}</strong>
                </p>
                <p className="text-gray-300 text-sm">
                  Tipo: <strong>{TIPOS_MANUTENCAO[dadosParaSalvar.tipo]?.label || dadosParaSalvar.tipo}</strong>
                </p>
                <p className="text-gray-300 text-sm">
                  Descrição: <strong>{dadosParaSalvar.descricao || 'N/A'}</strong>
                </p>
                <p className="text-gray-300 text-sm">
                  Custo: <strong className="text-emerald-400">R$ {dadosParaSalvar.custo?.toFixed(2) || '0,00'}</strong>
                </p>
              </div>
            )}

            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-red-300 text-sm font-medium flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Atenção: Esta ação é irreversível!
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={cancelarConfirmacao}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={executarSalvarManutencao}
              className="flex-1 bg-emerald-600 hover:bg-emerald-500 font-semibold"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Sim, Confirmar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManutencaoPageNova;
